import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  Tabs,
  Tab,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material'
import {
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as StableIcon,
  Speed as VelocityIcon,
  Schedule as AgingIcon,
  AttachMoney as ValuationIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  ShowChart as ChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useSnackbar } from 'notistack'
import { PageHeader, DataTable, StatusChip, TableColumn } from '../components/ui'
import { InventoryReportService } from '../services/inventoryReportService'
import type {
  StockHistoryReportResponse,
  StockHistoryFilters,
  SalesPatternReportResponse,
  SalesPatternFilters,
  StockValuationReportResponse,
  StockValuationFilters,
  StockAgingReportResponse,
  StockAgingFilters,
} from '../types/inventory'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-report-tabpanel-${index}`}
      aria-labelledby={`inventory-report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

const InventoryReportsMUI: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  // Pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)

  // Stock History Report State
  const [stockHistoryData, setStockHistoryData] = useState<StockHistoryReportResponse | null>(null)
  const [stockHistoryFilters, setStockHistoryFilters] = useState<StockHistoryFilters>({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end_date: new Date().toISOString().split('T')[0], // today
  })

  // Sales Pattern Report State
  const [salesPatternData, setSalesPatternData] = useState<SalesPatternReportResponse | null>(null)
  const [salesPatternFilters, setSalesPatternFilters] = useState<SalesPatternFilters>({})

  // Stock Valuation Report State
  const [stockValuationData, setStockValuationData] = useState<StockValuationReportResponse | null>(null)
  const [stockValuationFilters, setStockValuationFilters] = useState<StockValuationFilters>({})

  // Stock Aging Report State
  const [stockAgingData, setStockAgingData] = useState<StockAgingReportResponse | null>(null)
  const [stockAgingFilters, setStockAgingFilters] = useState<StockAgingFilters>({})

  // Performance Dashboard State
  const [performanceDashboardData, setPerformanceDashboardData] = useState<any>(null)

  // Load data when tab changes or filters change
  useEffect(() => {
    loadReportData()
  }, [activeTab, page, pageSize])

  const loadReportData = async () => {
    setIsLoading(true)
    try {
      switch (activeTab) {
        case 0:
          await loadStockHistoryReport()
          break
        case 1:
          await loadSalesPatternReport()
          break
        case 2:
          await loadStockValuationReport()
          break
        case 3:
          await loadStockAgingReport()
          break
        case 4:
          await loadPerformanceDashboard()
          break
      }
    } catch (error) {
      console.error('Failed to load report data:', error)
      enqueueSnackbar('Failed to load report data', { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadStockHistoryReport = async () => {
    const data = await InventoryReportService.getStockHistoryReport(
      stockHistoryFilters,
      pageSize,
      page * pageSize
    )
    setStockHistoryData(data)
  }

  const loadSalesPatternReport = async () => {
    const data = await InventoryReportService.getSalesPatternReport(
      salesPatternFilters,
      pageSize,
      page * pageSize
    )
    setSalesPatternData(data)
  }

  const loadStockValuationReport = async () => {
    const data = await InventoryReportService.getStockValuationReport(
      stockValuationFilters,
      pageSize,
      page * pageSize
    )
    setStockValuationData(data)
  }

  const loadStockAgingReport = async () => {
    const data = await InventoryReportService.getStockAgingReport(
      stockAgingFilters,
      pageSize,
      page * pageSize
    )
    setStockAgingData(data)
  }

  const loadPerformanceDashboard = async () => {
    const data = await InventoryReportService.getPerformanceDashboard()
    setPerformanceDashboardData(data)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setPage(0) // Reset pagination when switching tabs
  }

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      let blob: Blob
      let filename: string

      switch (activeTab) {
        case 0:
          blob = await InventoryReportService.exportStockHistoryReport(stockHistoryFilters, format)
          filename = `stock_history_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
          break
        case 1:
          blob = await InventoryReportService.exportSalesPatternReport(salesPatternFilters, format)
          filename = `sales_pattern_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
          break
        case 2:
          blob = await InventoryReportService.exportStockValuationReport(stockValuationFilters, format)
          filename = `stock_valuation_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
          break
        case 3:
          blob = await InventoryReportService.exportStockAgingReport(stockAgingFilters, format)
          filename = `stock_aging_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
          break
        case 4:
          blob = await InventoryReportService.exportPerformanceDashboard(format)
          filename = `inventory_performance_dashboard_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
          break
        default:
          throw new Error('Invalid tab')
      }

      InventoryReportService.downloadReportFile(blob, filename)
      enqueueSnackbar(`Report exported successfully as ${format.toUpperCase()}`, { variant: 'success' })
    } catch (error) {
      console.error('Export failed:', error)
      enqueueSnackbar('Failed to export report', { variant: 'error' })
    }
  }

  // Stock History Report Columns
  const stockHistoryColumns: TableColumn[] = [
    {
      id: 'date',
      label: 'Date',
      width: 120,
      render: (_, row) => (
        <Typography variant="body2">
          {new Date(row.date).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: 'product',
      label: 'Product',
      width: 200,
      render: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600} noWrap>
            {row.product_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.variant_name} • {row.variant_sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'movement_type',
      label: 'Type',
      width: 100,
      render: (_, row) => {
        const getMovementColor = (type: string) => {
          switch (type) {
            case 'purchase': return 'success'
            case 'sale': return 'primary'
            case 'adjustment': return 'warning'
            case 'transfer': return 'info'
            default: return 'default'
          }
        }
        return (
          <Chip
            label={row.movement_type}
            size="small"
            color={getMovementColor(row.movement_type) as any}
            variant="outlined"
          />
        )
      },
    },
    {
      id: 'quantity',
      label: 'Quantity',
      width: 100,
      align: 'right',
      render: (_, row) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={row.quantity > 0 ? 'success.main' : 'error.main'}
        >
          {row.quantity > 0 ? '+' : ''}{row.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'stock_before',
      label: 'Before',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary">
          {row.stock_before.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'stock_after',
      label: 'After',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          {row.stock_after.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'reference',
      label: 'Reference',
      width: 120,
      render: (_, row) => (
        <Typography variant="caption" color="text.secondary">
          {row.reference_id || '-'}
        </Typography>
      ),
    },
    {
      id: 'created_by',
      label: 'Created By',
      width: 120,
      render: (_, row) => (
        <Typography variant="caption" color="text.secondary">
          {row.created_by_admin_name || 'System'}
        </Typography>
      ),
    },
  ]

  // Sales Pattern Report Columns
  const salesPatternColumns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product',
      width: 200,
      render: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600} noWrap>
            {row.product_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.variant_name} • {row.variant_sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'sales_30d',
      label: '30d Sales',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.total_units_sold_30d.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'sales_60d',
      label: '60d Sales',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.total_units_sold_60d.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'sales_90d',
      label: '90d Sales',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.total_units_sold_90d.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'weekly_avg',
      label: 'Weekly Avg',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          {row.average_weekly_sales.toFixed(1)}
        </Typography>
      ),
    },
    {
      id: 'velocity',
      label: 'Velocity',
      width: 100,
      render: (_, row) => {
        const getVelocityColor = (velocity: string) => {
          switch (velocity) {
            case 'fast': return 'success'
            case 'medium': return 'warning'
            case 'slow': return 'error'
            default: return 'default'
          }
        }
        return (
          <Chip
            label={row.sales_velocity}
            size="small"
            color={getVelocityColor(row.sales_velocity) as any}
            icon={<VelocityIcon />}
          />
        )
      },
    },
    {
      id: 'trend',
      label: 'Trend',
      width: 120,
      render: (_, row) => {
        const getTrendIcon = (direction: string) => {
          switch (direction) {
            case 'increasing': return <TrendingUpIcon color="success" />
            case 'decreasing': return <TrendingDownIcon color="error" />
            case 'stable': return <StableIcon color="action" />
            default: return null
          }
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getTrendIcon(row.trend_direction)}
            <Typography variant="body2" color="text.secondary">
              {row.trend_percentage > 0 ? '+' : ''}{row.trend_percentage.toFixed(1)}%
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 'current_stock',
      label: 'Stock',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography
          variant="body2"
          color={row.current_stock <= 0 ? 'error.main' : 'text.primary'}
        >
          {row.current_stock.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'days_remaining',
      label: 'Days Left',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography
          variant="body2"
          color={row.days_of_stock_remaining <= 7 ? 'error.main' : row.days_of_stock_remaining <= 30 ? 'warning.main' : 'text.primary'}
        >
          {row.days_of_stock_remaining || 0}
        </Typography>
      ),
    },
  ]

  // Stock Valuation Report Columns
  const stockValuationColumns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product',
      width: 200,
      render: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600} noWrap>
            {row.product_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.variant_name} • {row.variant_sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'stock',
      label: 'Stock',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.current_stock.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'unit_cost',
      label: 'Unit Cost',
      width: 100,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          ₹{row.unit_cost.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'unit_retail',
      label: 'Unit Retail',
      width: 100,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          ₹{row.unit_retail_price.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'total_cost',
      label: 'Total Cost',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{row.total_cost_value.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'total_retail',
      label: 'Total Retail',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{row.total_retail_value.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'profit',
      label: 'Profit',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="success.main" fontWeight={500}>
          ₹{row.potential_profit.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'margin',
      label: 'Margin %',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          {row.profit_margin_percentage.toFixed(1)}%
        </Typography>
      ),
    },
    {
      id: 'abc_class',
      label: 'ABC',
      width: 60,
      render: (_, row) => {
        const getABCColor = (classification: string) => {
          switch (classification) {
            case 'A': return 'success'
            case 'B': return 'warning'
            case 'C': return 'error'
            default: return 'default'
          }
        }
        return (
          <Chip
            label={row.abc_classification}
            size="small"
            color={getABCColor(row.abc_classification) as any}
          />
        )
      },
    },
  ]

  // Stock Aging Report Columns
  const stockAgingColumns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product',
      width: 200,
      render: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600} noWrap>
            {row.product_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.variant_name} • {row.variant_sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'stock',
      label: 'Stock',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.current_stock.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'total_value',
      label: 'Total Value',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{row.total_value.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'avg_age',
      label: 'Avg Age',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2">
          {row.average_age_days} days
        </Typography>
      ),
    },
    {
      id: 'age_0_30',
      label: '0-30 Days',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="success.main">
          {row.age_0_30_days.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'age_31_60',
      label: '31-60 Days',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="info.main">
          {row.age_31_60_days.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'age_61_90',
      label: '61-90 Days',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="warning.main">
          {row.age_61_90_days.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'age_91_180',
      label: '91-180 Days',
      width: 100,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="error.main">
          {row.age_91_180_days.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'age_180_plus',
      label: '180+ Days',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Typography variant="body2" color="error.dark" fontWeight={600}>
          {row.age_over_180_days.quantity.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      width: 120,
      render: (_, row) => (
        <Stack direction="column" spacing={0.5}>
          {row.is_stale_inventory && (
            <StatusChip status="error" label="Stale" size="small" />
          )}
          {row.requires_attention && (
            <StatusChip status="warning" label="Attention" size="small" />
          )}
          {!row.is_stale_inventory && !row.requires_attention && (
            <StatusChip status="success" label="Good" size="small" />
          )}
        </Stack>
      ),
    },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title="Inventory Reports"
          subtitle="Comprehensive inventory analytics and reporting"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Inventory', path: '/inventory' },
            { label: 'Reports' },
          ]}
          actions={[
            {
              label: 'Refresh',
              onClick: loadReportData,
              variant: 'outlined',
              color: 'primary',
              icon: <RefreshIcon />,
            },
            {
              label: 'Export Excel',
              onClick: () => handleExport('excel'),
              variant: 'contained',
              color: 'success',
              icon: <ExportIcon />,
            },
            {
              label: 'Export PDF',
              onClick: () => handleExport('pdf'),
              variant: 'outlined',
              color: 'primary',
              icon: <ExportIcon />,
            },
          ]}
        />

        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="inventory reports tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label="Stock History"
                icon={<HistoryIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                label="Sales Patterns"
                icon={<TrendingUpIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                label="Stock Valuation"
                icon={<ValuationIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                label="Stock Aging"
                icon={<AgingIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                label="Performance Dashboard"
                icon={<DashboardIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tabs>
          </Box>

          {/* Loading indicator */}
          {isLoading && <LinearProgress />}

          {/* Stock History Report Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon />
                Stock History Report
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Track all stock movements with detailed transaction history
              </Typography>

              {/* Stock History Filters */}
              <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={stockHistoryFilters.start_date ? new Date(stockHistoryFilters.start_date) : null}
                      onChange={(date) => setStockHistoryFilters(prev => ({
                        ...prev,
                        start_date: date ? date.toISOString().split('T')[0] : undefined
                      }))}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="End Date"
                      value={stockHistoryFilters.end_date ? new Date(stockHistoryFilters.end_date) : null}
                      onChange={(date) => setStockHistoryFilters(prev => ({
                        ...prev,
                        end_date: date ? date.toISOString().split('T')[0] : undefined
                      }))}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      label="Movement Type"
                      value={stockHistoryFilters.movement_type || 'all'}
                      onChange={(e) => setStockHistoryFilters(prev => ({
                        ...prev,
                        movement_type: e.target.value === 'all' ? undefined : e.target.value
                      }))}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="purchase">Purchase</MenuItem>
                      <MenuItem value="sale">Sale</MenuItem>
                      <MenuItem value="adjustment">Adjustment</MenuItem>
                      <MenuItem value="transfer">Transfer</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      onClick={loadStockHistoryReport}
                      startIcon={<FilterIcon />}
                      fullWidth
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Card>

              {/* Stock History Summary */}
              {stockHistoryData && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {stockHistoryData.summary.total_movements.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Movements
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">
                        {stockHistoryData.summary.opening_stock.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Opening Stock
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="info.main">
                        {stockHistoryData.summary.closing_stock.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Closing Stock
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography
                        variant="h6"
                        color={stockHistoryData.summary.net_change >= 0 ? 'success.main' : 'error.main'}
                      >
                        {stockHistoryData.summary.net_change >= 0 ? '+' : ''}{stockHistoryData.summary.net_change.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Net Change
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>

            <DataTable
              columns={stockHistoryColumns}
              rows={stockHistoryData?.items || []}
              isLoading={isLoading}
              emptyMessage="No stock movements found for the selected period"
              page={page}
              rowsPerPage={pageSize}
              totalRows={stockHistoryData?.pagination.total || 0}
              onPageChange={setPage}
              onRowsPerPageChange={setPageSize}
              rowsPerPageOptions={[50, 100, 200]}
              getRowId={(row) => `${row.variant_id}-${row.date}-${row.movement_type}`}
            />
          </TabPanel>

          {/* Sales Pattern Report Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon />
                Sales Pattern Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Analyze sales velocity and identify fast/slow moving inventory
              </Typography>

              {/* Sales Pattern Filters */}
              <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      label="Sales Velocity"
                      value={salesPatternFilters.sales_velocity || 'all'}
                      onChange={(e) => setSalesPatternFilters(prev => ({
                        ...prev,
                        sales_velocity: e.target.value === 'all' ? undefined : e.target.value as any
                      }))}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="all">All Velocities</MenuItem>
                      <MenuItem value="fast">Fast Moving</MenuItem>
                      <MenuItem value="medium">Medium Moving</MenuItem>
                      <MenuItem value="slow">Slow Moving</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      label="Trend Direction"
                      value={salesPatternFilters.trend_direction || 'all'}
                      onChange={(e) => setSalesPatternFilters(prev => ({
                        ...prev,
                        trend_direction: e.target.value === 'all' ? undefined : e.target.value as any
                      }))}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="all">All Trends</MenuItem>
                      <MenuItem value="increasing">Increasing</MenuItem>
                      <MenuItem value="stable">Stable</MenuItem>
                      <MenuItem value="decreasing">Decreasing</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={salesPatternFilters.overstocked_only || false}
                          onChange={(e) => setSalesPatternFilters(prev => ({
                            ...prev,
                            overstocked_only: e.target.checked
                          }))}
                        />
                      }
                      label="Overstocked Only"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      onClick={loadSalesPatternReport}
                      startIcon={<FilterIcon />}
                      fullWidth
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Card>

              {/* Sales Pattern Summary */}
              {salesPatternData && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">
                        {salesPatternData.summary.fast_moving_count.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fast Moving
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main">
                        {salesPatternData.summary.medium_moving_count.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Medium Moving
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="error.main">
                        {salesPatternData.summary.slow_moving_count.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Slow Moving
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main">
                        {salesPatternData.summary.overstocked_count.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Overstocked
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>

            <DataTable
              columns={salesPatternColumns}
              rows={salesPatternData?.items || []}
              isLoading={isLoading}
              emptyMessage="No sales pattern data available"
              page={page}
              rowsPerPage={pageSize}
              totalRows={salesPatternData?.pagination.total || 0}
              onPageChange={setPage}
              onRowsPerPageChange={setPageSize}
              rowsPerPageOptions={[50, 100, 200]}
              getRowId={(row) => row.variant_id}
            />
          </TabPanel>

          {/* Stock Valuation Report Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ValuationIcon />
                Stock Valuation Report
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Analyze inventory value at cost and retail prices with ABC classification
              </Typography>

              {/* Stock Valuation Filters */}
              <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="ABC Classification"
                      value={stockValuationFilters.abc_classification || 'all'}
                      onChange={(e) => setStockValuationFilters(prev => ({
                        ...prev,
                        abc_classification: e.target.value === 'all' ? undefined : e.target.value as any
                      }))}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="all">All Classes</MenuItem>
                      <MenuItem value="A">Class A (High Value)</MenuItem>
                      <MenuItem value="B">Class B (Medium Value)</MenuItem>
                      <MenuItem value="C">Class C (Low Value)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Valuation Date"
                      value={stockValuationFilters.valuation_date ? new Date(stockValuationFilters.valuation_date) : new Date()}
                      onChange={(date) => setStockValuationFilters(prev => ({
                        ...prev,
                        valuation_date: date ? date.toISOString().split('T')[0] : undefined
                      }))}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      onClick={loadStockValuationReport}
                      startIcon={<FilterIcon />}
                      fullWidth
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Card>

              {/* Stock Valuation Summary */}
              {stockValuationData && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        ₹{(stockValuationData.summary.total_cost_value / 1000000).toFixed(1)}M
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Cost Value
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">
                        ₹{(stockValuationData.summary.total_retail_value / 1000000).toFixed(1)}M
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Retail Value
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">
                        ₹{(stockValuationData.summary.total_potential_profit / 1000000).toFixed(1)}M
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Potential Profit
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="info.main">
                        {stockValuationData.summary.average_profit_margin.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg Profit Margin
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>

            <DataTable
              columns={stockValuationColumns}
              rows={stockValuationData?.items || []}
              isLoading={isLoading}
              emptyMessage="No stock valuation data available"
              page={page}
              rowsPerPage={pageSize}
              totalRows={stockValuationData?.pagination.total || 0}
              onPageChange={setPage}
              onRowsPerPageChange={setPageSize}
              rowsPerPageOptions={[50, 100, 200]}
              getRowId={(row) => row.variant_id}
            />
          </TabPanel>

          {/* Stock Aging Report Tab */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AgingIcon />
                Stock Aging Report
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Identify old inventory and stale stock requiring attention
              </Typography>

              {/* Stock Aging Filters */}
              <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Age Bracket"
                      value={stockAgingFilters.age_bracket || 'all'}
                      onChange={(e) => setStockAgingFilters(prev => ({
                        ...prev,
                        age_bracket: e.target.value === 'all' ? undefined : e.target.value as any
                      }))}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="all">All Ages</MenuItem>
                      <MenuItem value="0-30">0-30 Days</MenuItem>
                      <MenuItem value="31-60">31-60 Days</MenuItem>
                      <MenuItem value="61-90">61-90 Days</MenuItem>
                      <MenuItem value="91-180">91-180 Days</MenuItem>
                      <MenuItem value="180+">180+ Days</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stockAgingFilters.stale_only || false}
                          onChange={(e) => setStockAgingFilters(prev => ({
                            ...prev,
                            stale_only: e.target.checked
                          }))}
                        />
                      }
                      label="Stale Inventory Only"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stockAgingFilters.attention_required_only || false}
                          onChange={(e) => setStockAgingFilters(prev => ({
                            ...prev,
                            attention_required_only: e.target.checked
                          }))}
                        />
                      }
                      label="Attention Required"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      onClick={loadStockAgingReport}
                      startIcon={<FilterIcon />}
                      fullWidth
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Card>

              {/* Stock Aging Summary */}
              {stockAgingData && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">
                        {stockAgingData.summary.age_breakdown.age_0_30_days.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        0-30 Days
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="info.main">
                        {stockAgingData.summary.age_breakdown.age_31_60_days.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        31-60 Days
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main">
                        {stockAgingData.summary.age_breakdown.age_61_90_days.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        61-90 Days
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="error.main">
                        {stockAgingData.summary.age_breakdown.age_91_180_days.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        91-180 Days
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="error.dark">
                        {stockAgingData.summary.age_breakdown.age_over_180_days.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        180+ Days
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="error.main">
                        {stockAgingData.summary.stale_inventory_count.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stale Items
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Aging Alert */}
              {stockAgingData && stockAgingData.summary.stale_inventory_count > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {stockAgingData.summary.stale_inventory_count} items have stale inventory (over 50% stock aged 90+ days).
                  Consider promotional pricing or liquidation strategies.
                </Alert>
              )}
            </Box>

            <DataTable
              columns={stockAgingColumns}
              rows={stockAgingData?.items || []}
              isLoading={isLoading}
              emptyMessage="No stock aging data available"
              page={page}
              rowsPerPage={pageSize}
              totalRows={stockAgingData?.pagination.total || 0}
              onPageChange={setPage}
              onRowsPerPageChange={setPageSize}
              rowsPerPageOptions={[50, 100, 200]}
              getRowId={(row) => row.variant_id}
            />
          </TabPanel>

          {/* Performance Dashboard Tab */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DashboardIcon />
                Inventory Performance Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Comprehensive overview of inventory performance metrics and KPIs
              </Typography>

              {performanceDashboardData && (
                <>
                  {/* Key Performance Indicators */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
                        color: 'white'
                      }}>
                        <Typography variant="h4" fontWeight={600}>
                          {performanceDashboardData.kpis.inventory_turnover_ratio.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Inventory Turnover Ratio
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Times per year
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                        color: 'white'
                      }}>
                        <Typography variant="h4" fontWeight={600}>
                          {performanceDashboardData.kpis.days_sales_outstanding.toFixed(0)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Days Sales Outstanding
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Average days
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                        color: 'white'
                      }}>
                        <Typography variant="h4" fontWeight={600}>
                          {performanceDashboardData.kpis.stockout_rate.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Stockout Rate
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Last 30 days
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
                        color: 'white'
                      }}>
                        <Typography variant="h4" fontWeight={600}>
                          ₹{(performanceDashboardData.kpis.carrying_cost_percentage * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Carrying Cost %
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Of inventory value
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Performance Metrics Grid */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* ABC Analysis Chart */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PieChartIcon />
                          ABC Analysis Distribution
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                          {Object.entries(performanceDashboardData.abc_analysis).map(([category, data]: [string, any]) => (
                            <Box key={category} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Chip 
                                label={`Class ${category.toUpperCase()}`}
                                color={category === 'a' ? 'success' : category === 'b' ? 'warning' : 'error'}
                                sx={{ minWidth: 80 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">
                                    {data.count} items
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {data.percentage.toFixed(1)}% value
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={data.percentage} 
                                  color={category === 'a' ? 'success' : category === 'b' ? 'warning' : 'error'}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Sales Velocity Distribution */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VelocityIcon />
                          Sales Velocity Distribution
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                          {Object.entries(performanceDashboardData.velocity_distribution).map(([velocity, data]: [string, any]) => (
                            <Box key={velocity} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Chip 
                                label={`${velocity.charAt(0).toUpperCase() + velocity.slice(1)} Moving`}
                                color={velocity === 'fast' ? 'success' : velocity === 'medium' ? 'warning' : 'error'}
                                sx={{ minWidth: 120 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">
                                    {data.count} items
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {data.percentage.toFixed(1)}%
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={data.percentage} 
                                  color={velocity === 'fast' ? 'success' : velocity === 'medium' ? 'warning' : 'error'}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Top Performers and Problem Areas */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Top Performing Products */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon />
                          Top Performing Products
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {performanceDashboardData.top_performers.map((product: any, index: number) => (
                            <Box key={product.variant_id} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              py: 1.5,
                              borderBottom: index < performanceDashboardData.top_performers.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {product.product_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.variant_sku}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  ₹{product.revenue.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.units_sold} units
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Problem Areas */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingDownIcon />
                          Areas Requiring Attention
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {performanceDashboardData.problem_areas.map((issue: any, index: number) => (
                            <Box key={index} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              py: 1.5,
                              borderBottom: index < performanceDashboardData.problem_areas.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {issue.issue_type}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {issue.description}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Chip 
                                  label={issue.severity}
                                  color={issue.severity === 'High' ? 'error' : issue.severity === 'Medium' ? 'warning' : 'info'}
                                  size="small"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {issue.affected_items} items
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Trend Analysis */}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Card sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ChartIcon />
                          Monthly Performance Trends
                        </Typography>
                        <Box sx={{ mt: 2, overflowX: 'auto' }}>
                          <Box sx={{ display: 'flex', gap: 2, minWidth: 800 }}>
                            {performanceDashboardData.monthly_trends.map((month: any) => (
                              <Card key={month.month} variant="outlined" sx={{ p: 2, minWidth: 150, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {month.month}
                                </Typography>
                                <Typography variant="h6" fontWeight={600} sx={{ my: 1 }}>
                                  ₹{(month.inventory_value / 1000000).toFixed(1)}M
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {month.trend === 'up' ? (
                                    <TrendingUpIcon fontSize="small" color="success" />
                                  ) : month.trend === 'down' ? (
                                    <TrendingDownIcon fontSize="small" color="error" />
                                  ) : (
                                    <StableIcon fontSize="small" color="action" />
                                  )}
                                  <Typography 
                                    variant="caption" 
                                    color={month.trend === 'up' ? 'success.main' : month.trend === 'down' ? 'error.main' : 'text.secondary'}
                                  >
                                    {month.change_percentage > 0 ? '+' : ''}{month.change_percentage.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Card>
                            ))}
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}

              {!performanceDashboardData && !isLoading && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Click refresh to load performance dashboard data
                </Alert>
              )}
            </Box>
          </TabPanel>
        </Card>
      </Box>
    </LocalizationProvider>
  )
}

export default InventoryReportsMUI